import { useCallback, useEffect, useState } from 'react';
import type { Issue, IssueDetail, ListFilters } from '@/models/issue';
import { issueApi } from '@/services/issueApi';

interface ListState {
  items: Issue[];
  total: number;
  loading: boolean;
  error: string;
  refresh: () => void;
}

export function useIssuesList(filters: ListFilters): ListState {
  const [items, setItems] = useState<Issue[]>([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [tick, setTick] = useState(0);
  const refresh = useCallback(() => setTick((t) => t + 1), []);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError('');
    issueApi
      .list(filters)
      .then((r) => {
        if (cancelled) return;
        setItems(r.items);
        setTotal(r.total);
      })
      .catch((e) => {
        if (cancelled) return;
        setError(e instanceof Error ? e.message : 'Liste konnte nicht geladen werden');
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [tick, filters.status, filters.severity, filters.component, filters.assignee, filters.q, filters.limit, filters.offset]);

  return { items, total, loading, error, refresh };
}

interface DetailState {
  issue: IssueDetail | null;
  loading: boolean;
  error: string;
  refresh: () => Promise<IssueDetail | null>;
}

export function useIssue(id: number): DetailState {
  const [issue, setIssue] = useState<IssueDetail | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const refresh = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const d = await issueApi.get(id);
      setIssue(d);
      return d;
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Issue konnte nicht geladen werden');
      return null;
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  return { issue, loading, error, refresh };
}

interface CountsState {
  total: number;
  open: number;
  loading: boolean;
}

export function useIssueCounts(deps: unknown[] = []): CountsState {
  const [state, setState] = useState<CountsState>({ total: 0, open: 0, loading: true });

  useEffect(() => {
    let cancelled = false;
    Promise.all([
      issueApi.list({ limit: 1 }).catch(() => null),
      issueApi.list({ limit: 1, status: 'open' }).catch(() => null),
    ]).then(([all, open]) => {
      if (cancelled) return;
      setState({
        total: all?.total ?? 0,
        open: open?.total ?? 0,
        loading: false,
      });
    });
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, deps);

  return state;
}
